import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, Save, Info } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

export default function AffiliatePayoutConfig() {
  const [cfg, setCfg] = useState({
    affiliate_commission_pct: 30,
    affiliate_period_days: 15,
    sub_affiliate_pct: 5,
    affiliate_min_payout: 0,
    affiliate_max_payout: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/aff-sub-config");
      setCfg({
        affiliate_commission_pct: r.data?.affiliate_commission_pct ?? 30,
        affiliate_period_days: r.data?.affiliate_period_days ?? 15,
        sub_affiliate_pct: r.data?.sub_affiliate_pct ?? 5,
        affiliate_min_payout: r.data?.affiliate_min_payout ?? 0,
        affiliate_max_payout: r.data?.affiliate_max_payout ?? 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/aff-sub-config", {
        affiliate_commission_pct: Number(cfg.affiliate_commission_pct),
        affiliate_period_days: Number(cfg.affiliate_period_days),
        sub_affiliate_pct: Number(cfg.sub_affiliate_pct),
        affiliate_min_payout: Number(cfg.affiliate_min_payout),
        affiliate_max_payout: Number(cfg.affiliate_max_payout),
      });
      toast.success("Configuration saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const Field = ({ label, name, suffix, min, max, step, help }) => (
    <div className="rounded-xl p-4 bg-[#0f1a2e] border border-white/10">
      <label className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">{label}</label>
      <div className="flex items-center gap-2 mt-2">
        <input
          type="number"
          value={cfg[name]}
          onChange={(e) => setCfg({ ...cfg, [name]: e.target.value })}
          min={min} max={max} step={step}
          className="flex-1 h-11 px-3 rounded-lg bg-black/30 border border-white/10 text-white text-[15px] font-bold"
          data-testid={`config-${name}`}
        />
        {suffix && <span className="text-white/55 text-[13px]">{suffix}</span>}
      </div>
      {help && <div className="text-[11px] text-white/45 mt-1.5 flex items-start gap-1"><Info size={10} className="mt-0.5" /> {help}</div>}
    </div>
  );

  return (
    <div data-testid="payout-config-page">
      <PageHeader
        eyebrow="AFFILIATE"
        title="Commission & Payout Config"
        subtitle="Tune commission rate, 15-day settlement cadence, sub-affiliate override, and min/max payout limits."
      />

      {loading ? (
        <div className="rounded-xl border border-white/10 p-10 text-center text-white/55">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Commission Rate (%)"
              name="affiliate_commission_pct"
              suffix="%" min={0} max={100} step={0.5}
              help="Default 30% of net loss (deposits - withdrawals)."
            />
            <Field
              label="Settlement Period"
              name="affiliate_period_days"
              suffix="days" min={1} max={90} step={1}
              help="Auto-credit job runs hourly and credits any completed cycle (max ~15 day lag)."
            />
            <Field
              label="Sub-Affiliate Override (%)"
              name="sub_affiliate_pct"
              suffix="%" min={0} max={100} step={0.5}
              help="Percentage of a sub-affiliate's commission that gets credited to their parent affiliate."
            />
            <Field
              label="Minimum Payout"
              name="affiliate_min_payout"
              suffix="৳" min={0} step={50}
              help="Minimum amount an affiliate can request in a single payout. Set 0 to disable."
            />
            <Field
              label="Maximum Payout"
              name="affiliate_max_payout"
              suffix="৳" min={0} step={500}
              help="Maximum amount in a single payout request. Set 0 to disable cap."
            />
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl text-[13px] font-bold"
              style={{ background: "linear-gradient(135deg, #d4af37, #b8941f)", color: "#0a070f" }}
              data-testid="save-payout-config"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Configuration"}
            </button>
            <div className="text-[11px] text-white/45">
              Policy: Negative carryover OFF · Manual payout approval only · Per-affiliate 15-day cycle anchored to registration
            </div>
          </div>
        </>
      )}
    </div>
  );
}
